import { initializeApp, type FirebaseOptions } from 'firebase/app';
import { getAuth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { JK_PRESENCE_FIREBASE_CONFIG } from './jkPresenceFirebaseConfig';

/** Same Firebase project as the Android app (`app/google-services.json`). */
export const JK_PRESENCE_PROJECT_ID = 'jk-presence';

function bakedEnvProjectId(): string {
  return process.env.REACT_APP_FIREBASE_PROJECT_ID || '';
}

function bakedEnvApiKey(): string {
  return process.env.REACT_APP_FIREBASE_API_KEY || '';
}

/**
 * Always use jk-presence web app keys from `jkPresenceFirebaseConfig.ts`.
 * CRA can bake a deleted CRM project's REACT_APP_FIREBASE_API_KEY from the shell;
 * mixing that key with projectId jk-presence causes auth/invalid-credential on login.
 */
export function resolveFirebaseConfig(): FirebaseOptions {
  const envProjectId = bakedEnvProjectId();
  const envApiKey = bakedEnvApiKey();

  if (envProjectId && envProjectId !== JK_PRESENCE_PROJECT_ID) {
    console.warn(
      `[JK Loom] Ignoring baked REACT_APP_FIREBASE_PROJECT_ID="${envProjectId}". ` +
        `Auth and Firestore use "${JK_PRESENCE_PROJECT_ID}" only.`,
    );
  }

  if (envApiKey && envApiKey !== JK_PRESENCE_FIREBASE_CONFIG.apiKey) {
    console.warn(
      '[JK Loom] Ignoring baked REACT_APP_FIREBASE_API_KEY (likely from deleted CRM project). ' +
        'Using jk-presence web app key from jkPresenceFirebaseConfig.ts.',
    );
  }

  return { ...JK_PRESENCE_FIREBASE_CONFIG };
}

export const firebaseConfig = resolveFirebaseConfig();
export const isWorkspaceFirebaseProject = true;

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);

setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.error('Failed to set auth persistence:', error);
});

export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app, 'asia-south1');

export default app;
